import type {
  ContactCustomFieldDef,
  PageCheckoutSettings,
} from "../types/pageFormSubmit";
import type { LeadCustomField } from "../models/Lead";

/** Sanitize field defs from a page / DB before persisting on Checkout. */
export function normalizePageContactCustomFields(
  settings: PageCheckoutSettings | undefined
): ContactCustomFieldDef[] | undefined {
  const list = settings?.contactCustomFields;
  if (!Array.isArray(list) || list.length === 0) return undefined;
  const out: ContactCustomFieldDef[] = [];
  for (const x of list) {
    if (x == null || typeof x !== "object") continue;
    const o = x as Record<string, unknown>;
    const key = typeof o.key === "string" ? o.key.trim() : "";
    const label = typeof o.label === "string" ? o.label.trim() : "";
    const options = Array.isArray(o.options)
      ? o.options.filter((e): e is string => typeof e === "string" && e.trim() !== "")
      : [];
    if (!key || !label || options.length === 0) continue;
    out.push({
      key,
      label,
      required: Boolean(o.required),
      options,
    });
  }
  return out.length > 0 ? out : undefined;
}

/** Stripe payment_method_types copied onto Checkout when the page Checkout action specifies them. */
export function normalizePageCheckoutPaymentMethodTypes(
  settings: PageCheckoutSettings | undefined
): string[] | undefined {
  const list = settings?.paymentMethodTypes;
  if (!Array.isArray(list) || list.length === 0) return undefined;
  const out = list
    .filter((x): x is string => typeof x === "string" && x.trim() !== "")
    .map((x) => x.trim());
  return out.length > 0 ? out : undefined;
}

/** When true, hosted checkout saves card via SetupIntent instead of charging. */
export function normalizePageCheckoutSavePaymentMethod(
  settings: PageCheckoutSettings | undefined
): boolean {
  return settings?.savePaymentMethod === true;
}

export const DEFAULT_CHECKOUT_SUBMIT_BTN_TEXT = "Place Order";

/** Submit button label copied onto Checkout when the page Checkout action sets it. */
export function normalizePageCheckoutSubmitBtnText(
  settings: PageCheckoutSettings | undefined
): string | undefined {
  const raw = settings?.submitBtnText;
  if (typeof raw !== "string") return undefined;
  const t = raw.trim();
  return t !== "" ? t : undefined;
}

export function resolveCheckoutSubmitBtnText(text?: string): string {
  const t = text?.trim();
  return t || DEFAULT_CHECKOUT_SUBMIT_BTN_TEXT;
}

/** Stripe `payment_method_configuration` id copied onto Checkout when the page action sets it. */
export function normalizePageCheckoutPaymentMethodConfigurationId(
  settings: PageCheckoutSettings | undefined
): string | undefined {
  const raw = settings?.paymentMethodConfigurationId;
  if (typeof raw !== "string") return undefined;
  const t = raw.trim();
  return t !== "" ? t : undefined;
}

export function validateContactCustomFieldSubmission({
  defs,
  rawValues,
}: {
  defs: ContactCustomFieldDef[];
  rawValues: unknown;
}):
  | { ok: true; setPairs: { key: string; value: string }[]; removeKeys: string[] }
  | { ok: false; error: string } {
  if (defs.length === 0) {
    if (
      rawValues != null &&
      typeof rawValues === "object" &&
      !Array.isArray(rawValues) &&
      Object.keys(rawValues as object).length > 0
    ) {
      return { ok: false, error: "Unexpected custom fields" };
    }
    return { ok: true, setPairs: [], removeKeys: [] };
  }

  if (rawValues != null && (typeof rawValues !== "object" || Array.isArray(rawValues))) {
    return { ok: false, error: "Invalid customFieldValues" };
  }

  const obj = (rawValues ?? {}) as Record<string, unknown>;
  const defKeys = new Set(defs.map((d) => d.key));
  for (const k of Object.keys(obj)) {
    if (!defKeys.has(k)) {
      return { ok: false, error: "Unknown custom field" };
    }
  }

  const setPairs: { key: string; value: string }[] = [];
  const removeKeys: string[] = [];

  for (const def of defs) {
    const raw = obj[def.key];
    const str =
      typeof raw === "string"
        ? raw.trim()
        : raw == null || raw === ""
          ? ""
          : String(raw);

    if (def.required) {
      if (!str || !def.options.includes(str)) {
        return {
          ok: false,
          error: `A valid choice is required for ${def.label}`,
        };
      }
      setPairs.push({ key: def.key, value: str });
    } else {
      if (!str) {
        removeKeys.push(def.key);
      } else if (!def.options.includes(str)) {
        return {
          ok: false,
          error: `Invalid choice for ${def.label}`,
        };
      } else {
        setPairs.push({ key: def.key, value: str });
      }
    }
  }

  return { ok: true, setPairs, removeKeys };
}

export function applyLeadCustomFieldPatch({
  existing,
  setPairs,
  removeKeys,
}: {
  existing: LeadCustomField[] | undefined;
  setPairs: { key: string; value: string }[];
  removeKeys: string[];
}): { key: string; value: string | number | Date | null }[] {
  const map = new Map<string, string | number | Date | null>();
  for (const f of existing ?? []) {
    map.set(f.key, f.value);
  }
  for (const k of removeKeys) {
    map.delete(k);
  }
  for (const p of setPairs) {
    map.set(p.key, p.value);
  }
  return Array.from(map.entries()).map(([key, value]) => ({ key, value }));
}

export function buildPaymentCustomFieldsFromLead({
  defs,
  leadCustomFields,
}: {
  defs: ContactCustomFieldDef[];
  leadCustomFields: LeadCustomField[] | undefined;
}): { key: string; value: string }[] {
  const byKey = new Map(
    (leadCustomFields ?? []).map((f) => [f.key, f.value])
  );
  return defs.map((d) => ({
    key: d.key,
    value: byKey.get(d.key) != null ? String(byKey.get(d.key)) : "",
  }));
}
